import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../styles/Admin.css";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Doughnut } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const KebunDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [tanggal, setTanggal] = useState("");
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [loadingMitra, setLoadingMitra] = useState(true);

  const [userInfo, setUserInfo] = useState({
    role: "Guest",
    name: "Guest",
    full_name: "Kebun Mandiri",
  });

  const [mitraStats, setMitraStats] = useState({
    total_mitra: 0,
    total_petani: 0,
    total_logistik: 0,
    total_pabrik: 0,
    total_kebun: 0,
  });

  // State Filter Waktu & Varietas Sawit
  const [filterPeriod, setFilterPeriod] = useState("7days");
  const [selectedVarietas, setSelectedVarietas] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [loadingPenjualan, setLoadingPenjualan] = useState(true);
  const [penjualanChartData, setPenjualanChartData] = useState(null);
  const [penjualanSummary, setPenjualanSummary] = useState({ total_ton: 0, total_rp: 0 });

  // State Donut Varietas
  const [distributionChartData, setDistributionChartData] = useState(null);
  const [loadingDonut, setLoadingDonut] = useState(true);

  // Fetch Data Grafik dengan Filter Waktu & Varietas
  const fetchPenjualanChart = useCallback(
    async (period, varietas, customStart = "", customEnd = "") => {
      setLoadingPenjualan(true);
      try {
        let url = `https://c-greenproject.org:7500/api/analytics/penjualan-tren?filter=${period}&jenis_sawit=${varietas}`;
        if (period === "custom" && customStart && customEnd) {
          url += `&start_date=${customStart}&end_date=${customEnd}`;
        }

        const res = await axios.get(url, { withCredentials: true });
        if (res.data.success) {
          setPenjualanSummary({
            total_ton: res.data.total_volume_ton,
            total_rp: res.data.total_pendapatan,
          });

          setPenjualanChartData({
            labels: res.data.labels,
            datasets: [
              {
                label: `Sawit Terjual (${varietas === "all" ? "Semua Varietas" : varietas.toUpperCase()})`,
                data: res.data.datasets.volume_ton,
                borderColor: varietas === "tenera" ? "#10b981" : varietas === "dura" ? "#3b82f6" : varietas === "pisifera" ? "#f59e0b" : "#10b981",
                backgroundColor:
                  varietas === "tenera"
                    ? "rgba(16, 185, 129, 0.12)"
                    : varietas === "dura"
                    ? "rgba(59, 130, 246, 0.12)"
                    : varietas === "pisifera"
                    ? "rgba(245, 158, 11, 0.12)"
                    : "rgba(16, 185, 129, 0.12)",
                borderWidth: 2.5,
                tension: 0.35,
                fill: true,
                pointBackgroundColor: "#ffffff",
                pointBorderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 6,
              },
            ],
          });
        }
      } catch (error) {
        console.error("Gagal mengambil tren penjualan:", error);
      } finally {
        setLoadingPenjualan(false);
      }
    },
    []
  );

  useEffect(() => {
    setTanggal(
      new Date().toLocaleDateString("id-ID", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    );

    axios
      .get("https://c-greenproject.org:7500/get_user_role", { withCredentials: true })
      .then((res) => {
        if (!res.data.error) {
          setUserInfo({
            role: res.data.role,
            name: res.data.name,
            full_name: res.data.full_name || res.data.name,
          });
        }
      })
      .catch((err) => console.error(err));

    axios
      .get("https://c-greenproject.org:7500/summary_mitra", { withCredentials: true })
      .then((res) => {
        if (res.data.success) setMitraStats(res.data.summary);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoadingMitra(false));

    axios
      .get("https://c-greenproject.org:7500/riwayat", { withCredentials: true })
      .then((res) => setTotalTransactions(res.data.length || 0))
      .catch((err) => console.error(err));

    axios
      .get("https://c-greenproject.org:7500/api/analytics/distribusi-jenis-sawit", { withCredentials: true })
      .then((res) => {
        if (res.data.success) {
          setDistributionChartData({
            labels: res.data.labels,
            datasets: [
              {
                data: res.data.data_luas,
                backgroundColor: ["#10b981", "#3b82f6", "#f59e0b", "#8b5cf6", "#64748b"],
                borderWidth: 2,
                borderColor: "#ffffff",
              },
            ],
          });
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoadingDonut(false));

    fetchPenjualanChart(filterPeriod, selectedVarietas);
  }, [fetchPenjualanChart, filterPeriod, selectedVarietas]);

  const handlePeriodChange = (period) => {
    setFilterPeriod(period);
    if (period !== "custom") {
      fetchPenjualanChart(period, selectedVarietas);
    }
  };

  const handleVarietasChange = (varietas) => {
    setSelectedVarietas(varietas);
    if (filterPeriod === "custom" && startDate && endDate) {
      fetchPenjualanChart("custom", varietas, startDate, endDate);
    } else if (filterPeriod !== "custom") {
      fetchPenjualanChart(filterPeriod, varietas);
    }
  };

  const handleCustomRangeSubmit = (e) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      Swal.fire("Peringatan", "Pilih rentang tanggal lengkap!", "warning");
      return;
    }
    fetchPenjualanChart("custom", selectedVarietas, startDate, endDate);
  };

  const handleLogout = async () => {
    try {
      await axios.post("https://c-greenproject.org:7500/logout", {}, { withCredentials: true });
      navigate("/");
    } catch (error) {
      console.error(error);
      Swal.fire("Gagal", "Gagal logout.", "error");
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100" style={{ backgroundColor: "#f8fafc" }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center px-4 py-2 bg-white border-bottom shadow-sm">
        <img src="/static/logo_potrait.png" alt="Logo" style={{ height: "38px" }} />
        <div className="d-flex align-items-center gap-3">
          <span
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/profile")}
            className="fw-semibold text-secondary small"
          >
            {userInfo.full_name}
          </span>
          <button className="btn btn-outline-danger btn-sm rounded-pill px-3 py-1" onClick={handleLogout}>
            Keluar
          </button>
        </div>
      </div>

      <div className="d-flex flex-grow-1">
        {/* Sidebar */}
        <div className="p-3 bg-white border-end" style={{ width: "240px", flexShrink: 0 }}>
          <p className="text-muted small mb-1">Portal Traceability</p>
          <h6 className="fw-bold mb-2 text-dark">Telsa Super Apps</h6>
          <p className="text-muted small d-flex align-items-center gap-2 mb-4" style={{ fontSize: "12px" }}>
            <img src="/static/schedule_11145854.png" alt="Date" width="14" />
            {tanggal}
          </p>

          <div className="d-flex flex-column gap-1">
            <div
              onClick={() => navigate("/kebun")}
              className={`p-2 rounded-3 d-flex align-items-center gap-2 ${
                location.pathname === "/kebun" ? "bg-success text-white fw-bold" : "text-dark"
              }`}
              style={{ cursor: "pointer", fontSize: "14px" }}
            >
              <img src="/static/eco-home_7633847.png" alt="Home" width="18" /> Beranda
            </div>
            <div
              onClick={() => navigate("/KebunP")}
              className={`p-2 rounded-3 d-flex align-items-center gap-2 ${
                location.pathname.toLowerCase().includes("kebunp") &&
                !location.pathname.toLowerCase().includes("kebunpabrik")
                  ? "bg-success text-white fw-bold"
                  : "text-dark"
              }`}
              style={{ cursor: "pointer", fontSize: "14px" }}
            >
              <img src="/static/Tanam.png" alt="Planting" width="18" /> Petani
            </div>
            <div
              onClick={() => navigate("/kebunL")}
              className={`p-2 rounded-3 d-flex align-items-center gap-2 ${
                location.pathname.toLowerCase().includes("kebunl")
                  ? "bg-success text-white fw-bold"
                  : "text-dark"
              }`}
              style={{ cursor: "pointer", fontSize: "14px" }}
            >
              <img src="/static/shipping.png" alt="Logistics" width="18" /> Logistik
            </div>
            <div
              onClick={() => navigate("/kebunpabrik")}
              className={`p-2 rounded-3 d-flex align-items-center gap-2 ${
                location.pathname === "/kebunpabrik" ? "bg-success text-white fw-bold" : "text-dark"
              }`}
              style={{ cursor: "pointer", fontSize: "14px" }}
            >
              <img src="/static/factory.png" alt="Factory" width="18" /> Pabrik
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-grow-1 p-4" style={{ width: "calc(100% - 240px)", overflowX: "hidden" }}>
          <div className="mb-4">
            <h2 className="fw-bold text-dark mb-1">Beranda</h2>
            <p className="text-secondary small mb-0">
              Selamat datang kembali, <strong>{userInfo.full_name}</strong>!
            </p>
          </div>

          {/* Metric Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
              gap: "16px",
              marginBottom: "24px",
              width: "100%",
            }}
          >
            {/* Card 1 */}
            <div className="bg-white rounded-4 shadow-sm border p-3 d-flex flex-column justify-content-between" style={{ minHeight: "160px" }}>
              <div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted fw-bold" style={{ fontSize: "11px" }}>TOTAL MITRA</span>
                  <span className="badge bg-success-subtle text-success rounded-pill px-2">🤝 Mitra</span>
                </div>
                <h3 className="fw-bold text-dark my-2 text-center">{loadingMitra ? "..." : mitraStats.total_mitra}</h3>
              </div>
              <div className="d-flex justify-content-between gap-1 pt-2 border-top" style={{ fontSize: "11px" }}>
                <span className="badge bg-light text-dark border flex-grow-1 text-center py-1" onClick={() => navigate("/KebunP")} style={{ cursor: "pointer" }}>
                  🌱 {mitraStats.total_petani}
                </span>
                <span className="badge bg-light text-dark border flex-grow-1 text-center py-1" onClick={() => navigate("/kebunL")} style={{ cursor: "pointer" }}>
                  🚚 {mitraStats.total_logistik}
                </span>
                <span className="badge bg-light text-dark border flex-grow-1 text-center py-1" onClick={() => navigate("/kebunpabrik")} style={{ cursor: "pointer" }}>
                  🏭 {mitraStats.total_pabrik}
                </span>
              </div>
            </div>

            {/* Card 2 */}
            <div
              className="bg-white rounded-4 shadow-sm border p-3 d-flex flex-column justify-content-between"
              style={{ minHeight: "160px", cursor: "pointer" }}
              onClick={() => navigate("/kebunt")}
            >
              <div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted fw-bold" style={{ fontSize: "11px" }}>TOTAL TRANSAKSI</span>
                  <span className="badge bg-danger-subtle text-danger rounded-pill px-2">📈 Riwayat</span>
                </div>
                <h3 className="fw-bold text-dark my-3 text-center">{totalTransactions}</h3>
              </div>
              <div className="pt-2 border-top text-center">
                <span className="text-success small fw-medium" style={{ fontSize: "12px" }}>Lihat riwayat →</span>
              </div>
            </div>

            {/* Card 3 */}
            <div
              className="bg-white rounded-4 shadow-sm border p-3 d-flex flex-column justify-content-between"
              style={{ minHeight: "160px", cursor: "pointer" }}
              onClick={() => navigate("/sawitk")}
            >
              <div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted fw-bold" style={{ fontSize: "11px" }}>INFORMASI HARGA</span>
                  <span className="badge bg-success-subtle text-success rounded-pill px-2">💰 Pasar</span>
                </div>
                <h5 className="fw-bold text-success my-3 text-center">Sawit & Pupuk</h5>
              </div>
              <div className="pt-2 border-top text-center">
                <span className="text-success small fw-medium" style={{ fontSize: "12px" }}>Cek pasar →</span>
              </div>
            </div>

            {/* Card 4 */}
            <div
              className="bg-white rounded-4 shadow-sm border p-3 d-flex flex-column justify-content-between"
              style={{ minHeight: "160px", cursor: "pointer" }}
              onClick={() => navigate("/logaktivitas")}
            >
              <div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted fw-bold" style={{ fontSize: "11px" }}>AUDIT LOG</span>
                  <span className="badge bg-primary-subtle text-primary rounded-pill px-2">📋 Log</span>
                </div>
                <h5 className="fw-bold text-primary my-3 text-center">Aktivitas User</h5>
              </div>
              <div className="pt-2 border-top text-center">
                <span className="text-primary small fw-medium" style={{ fontSize: "12px" }}>Lihat log →</span>
              </div>
            </div>
          </div>

          {/* Section Visualisasi */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2.1fr 1fr",
              gap: "20px",
              width: "100%",
            }}
          >
            {/* Line Chart Terjual */}
            <div className="bg-white rounded-4 shadow-sm border p-4 d-flex flex-column" style={{ minHeight: "410px" }}>
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <div>
                  <h6 className="fw-bold text-dark mb-0">Tren Penjualan Sawit</h6>
                  <span className="text-muted small" style={{ fontSize: "12px" }}>
                    Total: <strong className="text-success">{penjualanSummary.total_ton} Ton</strong> (Rp {penjualanSummary.total_rp.toLocaleString("id-ID")})
                  </span>
                </div>

                {/* Filter Controls (Varietas + Waktu) */}
                <div className="d-flex flex-wrap align-items-center gap-2">
                  {/* Dropdown Varietas */}
                  <select
                    className="form-select form-select-sm rounded-pill border shadow-none"
                    style={{ fontSize: "11px", width: "auto", minWidth: "135px" }}
                    value={selectedVarietas}
                    onChange={(e) => handleVarietasChange(e.target.value)}
                  >
                    <option value="all">🌿 Semua Varietas</option>
                    <option value="tenera">🌴 Tenera</option>
                    <option value="dura">🌴 Dura</option>
                    <option value="pisifera">🌴 Pisifera</option>
                  </select>

                  {/* Filter Periode Buttons */}
                  <div className="btn-group btn-group-sm p-1 bg-light rounded-pill border">
                    <button
                      type="button"
                      className={`btn rounded-pill px-2.5 py-1 border-0 ${filterPeriod === "7days" ? "btn-success text-white fw-semibold" : "btn-light text-secondary"}`}
                      style={{ fontSize: "11px" }}
                      onClick={() => handlePeriodChange("7days")}
                    >
                      7 Hari
                    </button>
                    <button
                      type="button"
                      className={`btn rounded-pill px-2.5 py-1 border-0 ${filterPeriod === "month" ? "btn-success text-white fw-semibold" : "btn-light text-secondary"}`}
                      style={{ fontSize: "11px" }}
                      onClick={() => handlePeriodChange("month")}
                    >
                      Bulan Ini
                    </button>
                    <button
                      type="button"
                      className={`btn rounded-pill px-2.5 py-1 border-0 ${filterPeriod === "year" ? "btn-success text-white fw-semibold" : "btn-light text-secondary"}`}
                      style={{ fontSize: "11px" }}
                      onClick={() => handlePeriodChange("year")}
                    >
                      Tahun Ini
                    </button>
                    <button
                      type="button"
                      className={`btn rounded-pill px-2.5 py-1 border-0 ${filterPeriod === "last_year" ? "btn-success text-white fw-semibold" : "btn-light text-secondary"}`}
                      style={{ fontSize: "11px" }}
                      onClick={() => handlePeriodChange("last_year")}
                    >
                      Tahun Lalu
                    </button>
                    <button
                      type="button"
                      className={`btn rounded-pill px-2.5 py-1 border-0 ${filterPeriod === "all" ? "btn-success text-white fw-semibold" : "btn-light text-secondary"}`}
                      style={{ fontSize: "11px" }}
                      onClick={() => handlePeriodChange("all")}
                    >
                      Semua
                    </button>
                    <button
                      type="button"
                      className={`btn rounded-pill px-2.5 py-1 border-0 ${filterPeriod === "custom" ? "btn-success text-white fw-semibold" : "btn-light text-secondary"}`}
                      style={{ fontSize: "11px" }}
                      onClick={() => handlePeriodChange("custom")}
                    >
                      Custom
                    </button>
                  </div>
                </div>
              </div>

              {/* Form Input Custom Date Range */}
              {filterPeriod === "custom" && (
                <form onSubmit={handleCustomRangeSubmit} className="d-flex align-items-center gap-2 mb-3 bg-light p-2 rounded-3 border">
                  <span className="text-secondary small fw-medium" style={{ fontSize: "11px" }}>Rentang:</span>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    style={{ fontSize: "11px", maxWidth: "140px" }}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                  <span className="text-muted small">-</span>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    style={{ fontSize: "11px", maxWidth: "140px" }}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                  <button type="submit" className="btn btn-sm btn-success rounded px-3 py-1" style={{ fontSize: "11px" }}>
                    Terapkan
                  </button>
                </form>
              )}

              {/* Canvas Line Chart */}
              <div style={{ flexGrow: 1, position: "relative", width: "100%", minHeight: "260px" }}>
                {loadingPenjualan ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <div className="spinner-border text-success spinner-border-sm" role="status"></div>
                  </div>
                ) : penjualanChartData ? (
                  <Line
                    data={penjualanChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: (context) => `Terjual: ${context.parsed.y} Ton`,
                          },
                        },
                      },
                      scales: {
                        x: {
                          grid: { display: false },
                          ticks: { font: { size: 11 }, color: "#64748b" },
                        },
                        y: {
                          beginAtZero: true,
                          grid: { color: "#f1f5f9" },
                          ticks: {
                            font: { size: 11 },
                            color: "#64748b",
                            callback: (val) => `${val} Ton`,
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <div className="d-flex justify-content-center align-items-center h-100 text-muted small">
                    Tidak ada data penjualan pada kriteria ini
                  </div>
                )}
              </div>
            </div>

            {/* Donut Chart: Varietas */}
            <div className="bg-white rounded-4 shadow-sm border p-4 d-flex flex-column" style={{ minHeight: "410px" }}>
              <div className="mb-3">
                <h6 className="fw-bold text-dark mb-0">Distribusi Varietas</h6>
                <span className="text-muted small" style={{ fontSize: "12px" }}>
                  Berdasarkan plot lahan aktif
                </span>
              </div>

              <div style={{ flexGrow: 1, position: "relative", width: "100%", minHeight: "260px" }} className="d-flex align-items-center justify-content-center">
                {loadingDonut ? (
                  <div className="spinner-border text-success spinner-border-sm" role="status"></div>
                ) : distributionChartData ? (
                  <Doughnut
                    data={distributionChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: "70%",
                      plugins: {
                        legend: {
                          position: "bottom",
                          labels: { boxWidth: 10, usePointStyle: true, font: { size: 11 } },
                        },
                      },
                    }}
                  />
                ) : (
                  <span className="text-muted small">Tidak ada data varietas</span>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default KebunDashboard;